import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface ViewerLoadingContextValue {
  isAnyPaneLoading: boolean;
  /** True once any pane has EVER reported loading. Distinguishes "all panes
   *  finished" from "no pane has started yet" — at mount, before the panes'
   *  effects run, isAnyPaneLoading is false for the wrong reason. */
  hasAnyPaneEverLoaded: boolean;
  reportPaneLoading: (paneIndex: number, isLoading: boolean) => void;
}

const ViewerLoadingContext = createContext<ViewerLoadingContextValue | null>(
  null
);

export function ViewerLoadingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loadingPanes, setLoadingPanes] = useState<Set<number>>(new Set());
  const [hasAnyPaneEverLoaded, setHasAnyPaneEverLoaded] = useState(false);

  const reportPaneLoading = useCallback(
    (paneIndex: number, isLoading: boolean) => {
      if (isLoading) setHasAnyPaneEverLoaded(true);
      setLoadingPanes((prev) => {
        const has = prev.has(paneIndex);
        if (isLoading && has) return prev;
        if (!isLoading && !has) return prev;
        const next = new Set(prev);
        if (isLoading) next.add(paneIndex);
        else next.delete(paneIndex);
        return next;
      });
    },
    []
  );

  const isAnyPaneLoading = loadingPanes.size > 0;

  const value = useMemo(
    () => ({ isAnyPaneLoading, hasAnyPaneEverLoaded, reportPaneLoading }),
    [isAnyPaneLoading, hasAnyPaneEverLoaded, reportPaneLoading]
  );

  return (
    <ViewerLoadingContext.Provider value={value}>
      {children}
    </ViewerLoadingContext.Provider>
  );
}

export function useViewerLoading(): ViewerLoadingContextValue {
  const context = useContext(ViewerLoadingContext);
  if (!context) {
    throw new Error(
      'useViewerLoading must be used within a ViewerLoadingProvider'
    );
  }
  return context;
}
